// frontend/src/components/ResourceEditModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-09-05
// Stage 2A:   2026-10-26 GMT — Vendor company selector for vendor/contractor resource updates.
// Updated:      2026-10-23
// Stage 2B: 2026-10-28 GMT — vendor contact linkage; full existing functionality retained.
//
// Description:
//   Centered edit modal for Resources with avatar upload (persistent), role lookup,
//   timezone, HR & commercials, persisted tenant cost centre taxonomy, and a map
//   with draggable pin that updates home_lat/home_lon (persisted on save).
//   • Map auto-zooms to postcode if present (backend geocode first, fallback Nominatim).
//   • INTERNAL USER AUTO-CREATION: When saving with a valid email and no user_id, the
//     backend auto-creates an Operator login (is_resource=true) using the same username
//     rule as Contacts. This modal exposes First/Last/Email and shows login status.
//   • LINKED USER ROLE EDITING: When a linked user exists, the selected role is persisted
//     to the real users.role_id via PUT /users/{id}. Resource system_role is also kept in sync.
//   • DRIFT WARNING: Flags resource/user mismatches for email and role so stale/broken records
//     are visible instead of silently misleading the UI.
//
// Endpoints used:
//   • PATCH /resources/{id}           (single source of truth update — includes avatar_url to prevent wipe)
//   • POST  /resources/{id}/avatar    (FormData key: file|avatar|image|photo — tries in order; Authorization)
//   • GET   /resources/{id}
//   • GET   /roles
//   • GET   /users/{id}
//   • PUT   /users/{id}              (persists linked login role/email)
//   • GET   /geocode?postcode=POSTCODE
//   • GET   /cost-centers/
//   • GET   /cost-centers/{id}/subtypes
//
// Notes:
//   • Uses plain Leaflet (no react-leaflet) for React 19 compatibility.
//   • Ensure 'leaflet/dist/leaflet.css' is imported once in your app root.
//
// Taxonomy integration (2026-10-23):
//   • Replaces temporary notes-based categories with tenant-scoped API categories/subtypes.
//   • Saves cost_center_id and cost_center_subtype_id; preserves existing notes.
//   • Prevents taxonomy changes from saving if reference data fails to load.
//
// Responsive pass (2026-09-16):
//   • Mobile-safe modal shell with internal body scrolling and persistent footer actions.
//   • Two-column resource form collapses to one column on narrow screens.
//   • Header/login/avatar/drift content wraps without forcing horizontal overflow.
//   • Home coordinates stack on mobile and the Leaflet map uses an adaptive height.
//   • Save, linked-user sync, avatar upload, geocoding, role and login logic are unchanged.

import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api/axiosInstance";
import VendorResourceContactLink from "./VendorResourceContactLink";
import { useTheme } from "../context/ThemeContext.jsx";
import L from "leaflet";

// Fix Leaflet marker icons via CDN
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  zIndex: 1000,
};
const modal = {
  width: "100%",
  maxWidth: 1100,
  minWidth: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
};
const header = {
  padding: "14px 16px",
  borderBottom: "1px solid #eee",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  minWidth: 0,
  flexShrink: 0,
};
const body = {
  padding: "clamp(12px, 2vw, 16px)",
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  overscrollBehavior: "contain",
};
const footer = {
  padding: 16,
  paddingBottom: "max(16px, env(safe-area-inset-bottom))",
  borderTop: "1px solid #eee",
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  flexWrap: "wrap",
  flexShrink: 0,
  background: "#fff",
};

const inputStyle = {
  height: 34,
  padding: "6px 8px",
  border: "1px solid #ccc",
  borderRadius: 6,
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};
const selectStyle = { ...inputStyle };
const textAreaStyle = { ...inputStyle, height: 90, resize: "vertical" };
const actionBtn = (bg) => ({
  padding: "8px 12px",
  border: "none",
  borderRadius: 8,
  color: "#fff",
  cursor: "pointer",
  fontWeight: 700,
});

const TIMEZONES = [
  "Europe/London","Europe/Berlin","Europe/Paris","UTC",
  "America/New_York","America/Los_Angeles","America/Chicago","America/Denver",
  "Asia/Kolkata","Asia/Tokyo","Asia/Singapore","Australia/Sydney",
];

// ---- helpers ----
const badge = (bg, color="#fff") => ({
  display: "inline-flex", alignItems: "center", gap: 6,
  maxWidth: "100%", whiteSpace: "normal", overflowWrap: "anywhere",
  fontSize: 12, fontWeight: 700, padding: "4px 8px", borderRadius: 999,
  background: bg, color,
});
const hint = { fontSize: 12, color: "#666", overflowWrap: "anywhere" };

const norm = (v) => String(v ?? "").trim();
const lower = (v) => norm(v).toLowerCase();
const toIntOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const ResourceEditModal = ({ resource, onClose, onSaved }) => {
  const { theme } = useTheme();
  const [form, setForm] = useState(() => ({
    id: resource?.id,
    first_name: resource?.first_name || "",
    last_name: resource?.last_name || "",
    email: resource?.email || "",
    mobile: resource?.mobile || "",
    job_title: resource?.job_title || "",
    resource_type: resource?.resource_type || "employee",
    vendor_customer_id: resource?.vendor_customer_id ?? "",
    employment_status: resource?.employment_status || "active",
    active: !!resource?.active,
    is_billable: !!resource?.is_billable,
    capacity_hours_per_week: resource?.capacity_hours_per_week ?? "",
    home_postcode: resource?.home_postcode || "",
    timezone: resource?.timezone || "Europe/London",
    location_id: resource?.location_id ?? "",
    department_id: resource?.department_id ?? "",
    manager_id: resource?.manager_id ?? "",
    working_calendar_id: resource?.working_calendar_id ?? "",
    hire_date: resource?.hire_date || "",
    leave_date: resource?.leave_date || "",
    cost_rate: resource?.cost_rate ?? "",
    default_bill_rate: resource?.default_bill_rate ?? "",
    burden_cost: resource?.burden_cost ?? "",
    identifier: resource?.identifier || "",
    system_role: resource?.system_role || "",
    notes: resource?.notes || "",
    avatar_url: resource?.avatar_url || "",
    cost_center_id: resource?.cost_center_id ?? null,
    cost_center_subtype_id: resource?.cost_center_subtype_id ?? null,
    home_lat: resource?.home_lat ?? 51.5074,
    home_lon: resource?.home_lon ?? -0.1278,
    user_id: resource?.user_id ?? null,
  }));
  const [vendorCompanies, setVendorCompanies] = useState([]);
  const [vendorCompaniesError, setVendorCompaniesError] = useState("");
  useEffect(() => {
    let alive = true;
    api.get("/customers/")
      .then(({ data }) => {
        if (!alive) return;
        const rows = Array.isArray(data) ? data : data?.items;
        if (!Array.isArray(rows)) throw new Error("Invalid customer response");
        setVendorCompanies(rows.filter((c) => c.is_vendor === true));
        setVendorCompaniesError("");
      })
      .catch((err) => {
        if (alive) setVendorCompaniesError(err?.response?.data?.detail || "Unable to load vendor companies");
      });
    return () => { alive = false; };
  }, []);

  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Live tenant taxonomy (IDs, never notes-based labels).
  const [costCenters, setCostCenters] = useState([]);
  const [costSubtypes, setCostSubtypes] = useState([]);
  const [costCentersLoading, setCostCentersLoading] = useState(true);
  const [costSubtypesLoading, setCostSubtypesLoading] = useState(false);
  const [taxonomyError, setTaxonomyError] = useState("");
  const [taxonomyReady, setTaxonomyReady] = useState(false);

  useEffect(() => {
    let active = true;
    setCostCentersLoading(true);
    setTaxonomyReady(false);
    api.get("/cost-centers/", { params: { limit: 500 } })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data) ? res.data : res.data?.items;
        if (!Array.isArray(rows)) throw new Error("Unexpected cost-centre response");
        setCostCenters(rows);
        setTaxonomyError("");
        setTaxonomyReady(true);
      })
      .catch((err) => {
        if (!active) return;
        setTaxonomyError(err?.response?.data?.detail || err?.message || "Unable to load cost centres");
        setCostCenters([]);
      })
      .finally(() => { if (active) setCostCentersLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const categoryId = toIntOrNull(form.cost_center_id);
    setCostSubtypes([]);
    setCostSubtypesLoading(Boolean(categoryId));
    if (!categoryId) return () => { active = false; };
    api.get(`/cost-centers/${categoryId}/subtypes`)
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data) ? res.data : res.data?.items;
        if (!Array.isArray(rows)) throw new Error("Unexpected subtype response");
        setCostSubtypes(rows);
        setTaxonomyError("");
      })
      .catch((err) => {
        if (!active) return;
        setTaxonomyError(err?.response?.data?.detail || err?.message || "Unable to load cost-centre subtypes");
        setCostSubtypes([]);
      })
      .finally(() => { if (active) setCostSubtypesLoading(false); });
    return () => { active = false; };
  }, [form.cost_center_id]);

  const changeCostCategory = (value) => {
    setTaxonomyError("");
    setForm((prev) => ({
      ...prev,
      cost_center_id: toIntOrNull(value),
      cost_center_subtype_id: null,
    }));
  };

  // Avatar preview state
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarDataUrl, setAvatarDataUrl] = useState(null);
  const [avatarTimestamp, setAvatarTimestamp] = useState(Date.now());
  const [imgVisible, setImgVisible] = useState(true);

  // Roles + linked user
  const [roles, setRoles] = useState([]);
  const [rolesErr, setRolesErr] = useState("");
  const [linkedUser, setLinkedUser] = useState(null);
  const [loginErr, setLoginErr] = useState("");

  // Server preview URL
  const previewUrl = `${window.location.origin}/api/resources/${form.id}/avatar/preview?ts=${avatarTimestamp}`;

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const actionNormal = "var(--btn-action-normal, #ff7a00)";
  const actionHover  = "var(--btn-action-hover,  #ff8f33)";

  const getRoleNameById = (roleId) => {
    const id = toIntOrNull(roleId);
    if (!id) return "";
    const hit = (Array.isArray(roles) ? roles : []).find((r) => Number(r?.id) === Number(id));
    return hit?.name ? String(hit.name) : "";
  };

  const getRoleIdByName = (roleName) => {
    const name = norm(roleName);
    if (!name) return null;
    const hit = (Array.isArray(roles) ? roles : []).find((r) => norm(r?.name) === name);
    return hit?.id ?? null;
  };

  // ——— load roles
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get("/roles");
        if (!active) return;
        const arr = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setRoles(arr);
      } catch (e) {
        setRolesErr(e?.response?.data?.detail || e?.message || "Roles not available");
        setRoles([]);
      }
    })();
    return () => { active = false; };
  }, []);

  // ——— load linked user
  useEffect(() => {
    let active = true;
    (async () => {
      if (!form.user_id) {
        setLinkedUser(null);
        setSelectedRoleId(getRoleIdByName(form.system_role));
        return;
      }
      try {
        const res = await api.get(`/users/${form.user_id}`);
        if (!active) return;
        const user = res.data || null;
        setLinkedUser(user);
        setLoginErr("");

        const authoritativeRoleId = toIntOrNull(user?.role_id);
        const authoritativeRoleName =
          norm(user?.role) || getRoleNameById(authoritativeRoleId) || norm(form.system_role);

        setSelectedRoleId(authoritativeRoleId || getRoleIdByName(authoritativeRoleName));
        if (authoritativeRoleName) {
          setForm((prev) => ({ ...prev, system_role: authoritativeRoleName }));
        }
      } catch (e) {
        if (!active) return;
        setLinkedUser(null);
        setSelectedRoleId(getRoleIdByName(form.system_role));
        setLoginErr(e?.response?.data?.detail || e?.message || "Unable to load linked user");
      }
    })();
    return () => { active = false; };
  }, [form.user_id, roles]);

  const selectedRoleName = useMemo(() => {
    return getRoleNameById(selectedRoleId) || norm(form.system_role);
  }, [selectedRoleId, form.system_role, roles]);

  const linkedRoleName = useMemo(() => {
    return norm(linkedUser?.role) || getRoleNameById(linkedUser?.role_id) || "";
  }, [linkedUser, roles]);

  const emailDrift = Boolean(form.user_id && linkedUser && lower(form.email) !== lower(linkedUser?.email));
  const roleDrift = Boolean(form.user_id && linkedUser && selectedRoleName && linkedRoleName && selectedRoleName !== linkedRoleName);
  const hasDrift = emailDrift || roleDrift;

  // ——— avatar pick
  const handleAvatarPick = (file) => {
    if (!file) {
      setAvatarFile(null);
      setAvatarDataUrl(null);
      setImgVisible(true);
      setAvatarTimestamp(Date.now());
      return;
    }
    setAvatarFile(file);
    setImgVisible(true);
    const reader = new FileReader();
    reader.onload = (e) => setAvatarDataUrl(e.target.result);
    reader.readAsDataURL(file);
  };

  // ——— refetch resource
  const reloadResource = async (id) => {
    try {
      const res = await api.get(`/resources/${id}`);
      if (res?.data) {
        setForm((prev) => ({ ...prev, ...res.data }));
        setAvatarTimestamp(Date.now());
        setImgVisible(true);
      }
    } catch (e) {
      console.warn("Reload resource failed:", e?.response?.data || e?.message || e);
    }
  };

  // ---------- Map ----------
  const mapRef = useRef(null);
  const mapObjRef = useRef(null);
  const markerRef = useRef(null);

  const centerOn = (lat, lon, zoom = 12) => {
    const map = mapObjRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    const target = L.latLng(lat, lon);
    marker.setLatLng(target);
    map.setView(target, zoom, { animate: true });
  };

  useEffect(() => {
    if (mapObjRef.current || !mapRef.current) return;
    const lat = Number(form.home_lat) || 51.5074;
    const lon = Number(form.home_lon) || -0.1278;
    const center = [lat, lon];

    const map = L.map(mapRef.current);
    map.setView(center, 12);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker(center, { draggable: true }).addTo(map);
    marker.on("dragend", () => {
      const p = marker.getLatLng();
      setField("home_lat", Number(p.lat.toFixed(6)));
      setField("home_lon", Number(p.lng.toFixed(6)));
    });
    map.on("click", (e) => {
      marker.setLatLng(e.latlng);
      setField("home_lat", Number(e.latlng.lat.toFixed(6)));
      setField("home_lon", Number(e.latlng.lng.toFixed(6)));
    });

    mapObjRef.current = map;
    markerRef.current = marker;

    return () => {
      map.off(); map.remove();
      mapObjRef.current = null;
      markerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapObjRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    const lat = Number(form.home_lat);
    const lon = Number(form.home_lon);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      const next = L.latLng(lat, lon);
      const cur = marker.getLatLng();
      if (Math.abs(cur.lat - next.lat) > 1e-6 || Math.abs(cur.lng - next.lng) > 1e-6) {
        marker.setLatLng(next);
        map.panTo(next, { animate: true });
      }
    }
  }, [form.home_lat, form.home_lon]);

  // ---------- Postcode geocode ----------
  const geocodeAbortRef = useRef(null);
  const geocodePostcode = async (postcode) => {
    if (!postcode || !postcode.trim()) return null;
    if (geocodeAbortRef.current) geocodeAbortRef.current.abort();
    const ctrl = new AbortController();
    geocodeAbortRef.current = ctrl;

    try {
      const r = await api.get("/geocode", { params: { postcode }, signal: ctrl.signal });
      const { lat, lon } =
        r?.data?.lat && r?.data?.lon
          ? r.data
          : Array.isArray(r?.data) && r.data.length
          ? { lat: r.data[0].lat, lon: r.data[0].lon }
          : {};
      if (lat && lon) return { lat: Number(lat), lon: Number(lon) };
    } catch {}

    try {
      const resp = await fetch(
        "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" + encodeURIComponent(postcode),
        { signal: ctrl.signal, headers: { "Accept-Language": "en-GB" } }
      );
      const arr = await resp.json();
      if (Array.isArray(arr) && arr.length) {
        return { lat: Number(arr[0].lat), lon: Number(arr[0].lon) };
      }
    } catch {}
    return null;
  };

  useEffect(() => {
    let debTimer;
    const shouldGeocode =
      form.home_postcode &&
      (form.home_lat === null ||
        form.home_lon === null ||
        (Math.abs(Number(form.home_lat)) < 0.0001 && Math.abs(Number(form.home_lon)) < 0.0001));

    if (form.home_postcode && shouldGeocode) {
      debTimer = setTimeout(async () => {
        const out = await geocodePostcode(form.home_postcode);
        if (out?.lat && out?.lon) {
          setField("home_lat", Number(out.lat.toFixed(6)));
          setField("home_lon", Number(out.lon.toFixed(6)));
          centerOn(out.lat, out.lon, 13);
        }
      }, 350);
    }
    return () => { if (debTimer) clearTimeout(debTimer); };
  }, []);

  useEffect(() => {
    if (!form.home_postcode) return;
    const deb = setTimeout(async () => {
      const out = await geocodePostcode(form.home_postcode);
      if (out?.lat && out?.lon) {
        setField("home_lat", Number(out.lat.toFixed(6)));
        setField("home_lon", Number(out.lon.toFixed(6)));
        centerOn(out.lat, out.lon, 13);
      }
    }, 500);
    return () => clearTimeout(deb);
  }, [form.home_postcode]);

  // ---------- Avatar upload ----------
  const uploadAvatarFile = async () => {
    if (!avatarFile) return false;
    const keys = ["file", "avatar", "image", "photo"];
    const token = localStorage.getItem("token");

    for (const key of keys) {
      const fd = new FormData();
      fd.append(key, avatarFile);
      try {
        await api.post(`/resources/${form.id}/avatar`, fd, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "multipart/form-data",
          },
        });
        return true;
      } catch (e) {
        console.warn(`[ResourceEditModal] Upload failed for key "${key}":`, e?.response?.data || e?.message || e);
      }
    }
    return false;
  };

  const emailNeededForLogin = !form.user_id && (!form.email || !/.+@.+\..+/.test(form.email));

  const updateLinkedUserIfNeeded = async () => {
    if (!form.user_id) return null;

    const effectiveRoleId = toIntOrNull(selectedRoleId) || getRoleIdByName(form.system_role);
    const roleName = getRoleNameById(effectiveRoleId) || norm(form.system_role);

    if (!effectiveRoleId) {
      throw new Error("A valid linked user role is required.");
    }

    const userPayload = {
      first_name: form.first_name || null,
      last_name: form.last_name || null,
      email: form.email || null,
      role_id: effectiveRoleId,
      role: roleName || null,
      is_active: !!form.active,
      avatar_url: form.avatar_url || null,
    };

    const token = localStorage.getItem("token");
    const res = await api.put(`/users/${form.user_id}`, userPayload, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    });

    return res?.data || null;
  };

  const onSave = async () => {
    try {
      setSaving(true);

      if (emailNeededForLogin) {
        if (!window.confirm("This resource has no login yet. Add a valid email to auto-create an Operator account. Continue without creating a login?")) {
          setSaving(false);
          return;
        }
      }

      // 1) AVATAR FIRST
      let avatarUrlForPatch = form.avatar_url || null;
      if (avatarFile) {
        const ok = await uploadAvatarFile();
        if (ok) {
          const afterUpload = await api.get(`/resources/${form.id}`);
          const serverAvatar = afterUpload?.data?.avatar_url || null;
          if (serverAvatar) {
            avatarUrlForPatch = serverAvatar;
            setField("avatar_url", serverAvatar);
          }
          setAvatarTimestamp(Date.now());
          setAvatarDataUrl(null);
          setImgVisible(true);
        }
      }

      // 2) Validate live taxonomy before persisting IDs.
      if (!taxonomyReady || costCentersLoading || costSubtypesLoading || taxonomyError) {
        throw new Error(taxonomyError || "Cost-centre taxonomy is not ready. Please retry.");
      }
      const categoryId = toIntOrNull(form.cost_center_id);
      const subtypeId = toIntOrNull(form.cost_center_subtype_id);
      if (categoryId && !costCenters.some((c) => Number(c.id) === categoryId)) {
        throw new Error("The selected cost-centre category is unavailable.");
      }
      if (subtypeId && (!categoryId || !costSubtypes.some(
        (sub) => Number(sub.id) === subtypeId && Number(sub.cost_center_id) === categoryId
      ))) {
        throw new Error("Select a valid subtype for the chosen cost-centre category.");
      }

      // 3) PATCH resource
      const effectiveRoleId = toIntOrNull(selectedRoleId) || getRoleIdByName(form.system_role);
      const effectiveRoleName = getRoleNameById(effectiveRoleId) || norm(form.system_role) || null;

      const payload = {
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        email: form.email || null,
        mobile: form.mobile || null,
        job_title: form.job_title || null,
        resource_type: form.resource_type || null,
        vendor_customer_id: form.vendor_customer_id ? Number(form.vendor_customer_id) : null,
        employment_status: form.employment_status || "active",
        active: !!form.active,
        is_billable: !!form.is_billable,
        capacity_hours_per_week: form.capacity_hours_per_week ? Number(form.capacity_hours_per_week) : null,
        home_postcode: form.home_postcode || null,
        timezone: form.timezone || "Europe/London",
        location_id: form.location_id || null,
        department_id: form.department_id || null,
        manager_id: form.manager_id || null,
        working_calendar_id: form.working_calendar_id || null,
        hire_date: form.hire_date || null,
        leave_date: form.leave_date || null,
        cost_rate: form.cost_rate !== "" ? Number(form.cost_rate) : null,
        default_bill_rate: form.default_bill_rate !== "" ? Number(form.default_bill_rate) : null,
        burden_cost: form.burden_cost !== "" ? Number(form.burden_cost) : null,
        identifier: form.identifier || null,
        system_role: effectiveRoleName,
        notes: form.notes || "",
        cost_center_id: categoryId,
        cost_center_subtype_id: subtypeId,
        home_lat: form.home_lat !== "" ? Number(form.home_lat) : null,
        home_lon: form.home_lon !== "" ? Number(form.home_lon) : null,
        avatar_url: avatarUrlForPatch,
      };

      const token = localStorage.getItem("token");
      await api.patch(`/resources/${form.id}`, payload, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });

      // 4) PATCH linked user role/email if linked user exists
      const updatedLinkedUser = await updateLinkedUserIfNeeded();
      if (updatedLinkedUser) {
        setLinkedUser(updatedLinkedUser);
      }

      // 5) Reload authoritative state
      await reloadResource(form.id);
      if (form.user_id) {
        try {
          const linked = await api.get(`/users/${form.user_id}`);
          setLinkedUser(linked?.data || null);
        } catch (e) {
          setLinkedUser(null);
          setLoginErr(e?.response?.data?.detail || e?.message || "Unable to reload linked user");
        }
      }

      onSaved && onSaved();
    } catch (e) {
      alert(`Update failed: ${e?.response?.data?.detail || e?.response?.data || e?.message || "Unknown error"}`);
    } finally {
      setSaving(false);
    }
  };

  const createLoginNow = async () => { await onSave(); };

  return (
    <div style={overlay} className="p-2 sm:p-4" onClick={onClose}>
      <div
        style={modal}
        className="max-h-[calc(100dvh-1rem)] sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={header}>
          <div className="min-w-0 flex-1" style={{ display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
            <div style={{
              width: 40, height: 40, borderRadius: "50%", background: "#f2f2f2",
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "1px solid #ddd", fontSize: 14, color: "#777", fontWeight: 700,
            }}>
              {(form.first_name?.[0] || "").toUpperCase()}{(form.last_name?.[0] || "").toUpperCase()}
            </div>
            {avatarDataUrl ? (
              <img
                src={avatarDataUrl}
                alt="avatar"
                style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "1px solid #ddd", position: "absolute", left: 0, top: 0, display: imgVisible ? "block" : "none" }}
                onError={() => setImgVisible(false)}
                onLoad={() => setImgVisible(true)}
              />
            ) : (
              <img
                src={previewUrl}
                alt="avatar"
                style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "1px solid #ddd", position: "absolute", left: 0, top: 0, display: imgVisible ? "block" : "none" }}
                onError={() => setImgVisible(false)}
                onLoad={() => setImgVisible(true)}
              />
            )}
            <div className="ml-0 min-w-0 flex-1 sm:ml-[52px]">
              <div className="break-words" style={{ fontWeight: 700 }}>
                {(form.first_name || "") + " " + (form.last_name || "")}
              </div>
              <div className="break-all" style={{ fontSize: 12, color: "#666" }}>{form.email || "—"}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ padding: "6px 10px", borderRadius: 999, background: "transparent", color: "#333", border: "1px solid #ddd", cursor: "pointer", fontWeight: 700, flexShrink: 0 }}
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={body}>
          {/* 🔐 Login */}
          <div className="min-w-0" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {form.user_id ? (
              <span style={badge("#10b981")}>Has login: {linkedUser?.username || "loading…"}</span>
            ) : (
              <span style={badge("#ef4444")}>No login</span>
            )}
            {loginErr && <span style={{ ...hint, color: "#b91c1c" }}>{loginErr}</span>}
            {!form.user_id && (
              <>
                <button
                  onClick={createLoginNow}
                  className="w-full sm:w-auto"
                  style={actionBtn("#2563eb")}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#1d4ed8")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#2563eb")}
                  title="Creates an Operator user when an email is provided"
                  disabled={!form.email || saving}
                >
                  Create Operator Login
                </button>
                <span style={hint}>Provide an email, then click to create. Saving also creates automatically.</span>
              </>
            )}
            {form.user_id && <span style={hint}>Toggling <b>Active</b> will enable/disable the linked user account.</span>}
          </div>

          {hasDrift ? (
            <div style={{
              marginBottom: 14,
              padding: "10px 12px",
              border: "1px solid #f59e0b",
              background: "#fffbeb",
              borderRadius: 8,
              color: "#92400e",
              fontSize: 12,
              lineHeight: 1.5,
              overflowWrap: "anywhere",
            }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>Linked login drift detected</div>
              {emailDrift ? (
                <div>
                  Email mismatch: resource has <b>{form.email || "—"}</b> but linked user has <b>{linkedUser?.email || "—"}</b>.
                </div>
              ) : null}
              {roleDrift ? (
                <div>
                  Role mismatch: resource shows <b>{selectedRoleName || "—"}</b> but linked user is <b>{linkedRoleName || "—"}</b>.
                </div>
              ) : null}
              <div style={{ marginTop: 4 }}>
                Saving this modal will sync the linked user to the selected resource email/role.
              </div>
            </div>
          ) : null}

          <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2">
            {/* Identity */}
            <div><label>First Name</label><input style={inputStyle} value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} /></div>
            <div><label>Last Name</label><input style={inputStyle} value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} /></div>
            <div>
              <label>Email { !form.user_id && <span style={{ color:"#b91c1c" }}>* (required to create login)</span> }</label>
              <input
                style={{ ...inputStyle, borderColor: (!form.user_id && (!form.email || !/.+@.+\..+/.test(form.email))) ? "#ef4444" : "#ccc" }}
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="name@company.com"
              />
            </div>
            <div className="hidden md:block" />

            {/* Contact */}
            <div><label>Mobile</label><input style={inputStyle} value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} /></div>
            <div><label>Job Title</label><input style={inputStyle} value={form.job_title} onChange={(e) => setField("job_title", e.target.value)} /></div>

            {/* Status & Type */}
            <div>
              <label>Employment Status</label>
              <select style={selectStyle} value={form.employment_status} onChange={(e) => setField("employment_status", e.target.value)}>
                <option value="active">Active</option>
                <option value="on_leave">On Leave</option>
                <option value="terminated">Terminated</option>
                <option value="suspended">Suspended</option>
                <option value="contractor">Contractor</option>
              </select>
            </div>
            <div>
              <label>Resource Type</label>
              <select style={selectStyle} value={form.resource_type} onChange={(e) => setField("resource_type", e.target.value)}>
                <option value="employee">Employee</option>
                <option value="contractor">Contractor</option>
                <option value="vendor">Vendor</option>
              </select>
            </div>

            {(["vendor", "contractor"].includes(form.resource_type)) && (
              <div>
                <label>Vendor company (optional)</label>
                <select style={selectStyle} disabled={!!vendorCompaniesError} value={form.vendor_customer_id ?? ""} onChange={(e) => setField("vendor_customer_id", e.target.value)}>
                  <option value="">No vendor company</option>
                  {vendorCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {vendorCompaniesError && <small role="alert">{vendorCompaniesError}</small>}
              </div>
            )}
            {/* System Role */}
            <div>
              <label>System Role {form.user_id ? <span style={{ color: "#666", fontSize: 12 }}>(linked user)</span> : null}</label>
              {roles.length > 0 ? (
                <select
                  style={selectStyle}
                  value={selectedRoleId || ""}
                  onChange={(e) => {
                    const nextId = toIntOrNull(e.target.value);
                    setSelectedRoleId(nextId);
                    setField("system_role", getRoleNameById(nextId));
                  }}
                >
                  <option value="">(none)</option>
                  {roles.map((r) => (
                    <option key={r.id ?? r.name} value={r.id}>{r.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  style={inputStyle}
                  value={form.system_role}
                  onChange={(e) => setField("system_role", e.target.value)}
                  placeholder={rolesErr ? `Fallback — ${rolesErr}` : "(type a role)"}
                />
              )}
            </div>
            <div><label>Identifier</label><input style={inputStyle} value={form.identifier} onChange={(e) => setField("identifier", e.target.value)} placeholder="e.g., RES-000123" /></div>

            {/* Flags */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input id="res-active" type="checkbox" checked={form.active} onChange={(e) => setField("active", e.target.checked)} />
              <label htmlFor="res-active">Active</label>
            </div>
            <VendorResourceContactLink
              resourceId={form.id}
              enabled={["vendor", "contractor"].includes(form.resource_type) && !!form.vendor_customer_id && Number(form.vendor_customer_id) === Number(resource?.vendor_customer_id)}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input id="res-billable" type="checkbox" checked={form.is_billable} onChange={(e) => setField("is_billable", e.target.checked)} />
              <label htmlFor="res-billable">Billable</label>
            </div>

            {/* Capacity & Time */}
            <div><label>Capacity (hrs/week)</label><input type="number" style={inputStyle} value={form.capacity_hours_per_week} onChange={(e) => setField("capacity_hours_per_week", e.target.value)} /></div>
            <div>
              <label>Timezone</label>
              <select style={selectStyle} value={form.timezone} onChange={(e) => setField("timezone", e.target.value)}>
                {TIMEZONES.map((tz) => (<option key={tz} value={tz}>{tz}</option>))}
              </select>
            </div>

            {/* Location basics */}
            <div><label>Home Postcode</label><input style={inputStyle} value={form.home_postcode} onChange={(e) => setField("home_postcode", e.target.value)} placeholder="e.g., SW1A 1AA" /></div>
            <div><label>Hire Date</label><input type="date" style={inputStyle} value={form.hire_date || ""} onChange={(e) => setField("hire_date", e.target.value)} /></div>
            <div><label>Leave Date</label><input type="date" style={inputStyle} value={form.leave_date || ""} onChange={(e) => setField("leave_date", e.target.value)} /></div>

            {/* Commercials */}
            <div><label>Cost Rate</label><input type="number" step="0.01" style={inputStyle} value={form.cost_rate} onChange={(e) => setField("cost_rate", e.target.value)} /></div>
            <div><label>Default Bill Rate</label><input type="number" step="0.01" style={inputStyle} value={form.default_bill_rate} onChange={(e) => setField("default_bill_rate", e.target.value)} /></div>
            <div><label>Burden Cost</label><input type="number" step="0.01" style={inputStyle} value={form.burden_cost} onChange={(e) => setField("burden_cost", e.target.value)} /></div>

            {/* Persisted tenant cost-centre taxonomy */}
            <div>
              <label>Cost Centre (Category)</label>
              <select
                style={selectStyle}
                value={form.cost_center_id ?? ""}
                disabled={costCentersLoading || !taxonomyReady || saving}
                onChange={(e) => changeCostCategory(e.target.value)}
              >
                <option value="">(Unclassified)</option>
                {costCenters.filter((cat) => cat.active || Number(cat.id) === Number(form.cost_center_id)).map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}{cat.active ? "" : " (inactive)"}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Cost Centre (Subtype)</label>
              <select
                style={selectStyle}
                value={form.cost_center_subtype_id ?? ""}
                disabled={!form.cost_center_id || costSubtypesLoading || !!taxonomyError || saving}
                onChange={(e) => setField("cost_center_subtype_id", toIntOrNull(e.target.value))}
              >
                <option value="">(No subtype)</option>
                {costSubtypes.filter((sub) => sub.active || Number(sub.id) === Number(form.cost_center_subtype_id)).map((sub) => (
                  <option key={sub.id} value={sub.id}>{sub.name}{sub.active ? "" : " (inactive)"}</option>
                ))}
              </select>
            </div>
            {taxonomyError && (
              <div className="min-w-0 md:col-span-2" role="alert" style={{ color: "#b91c1c", fontSize: 12 }}>
                Cost-centre taxonomy unavailable: {String(taxonomyError)}. Saving is disabled until this is resolved.
              </div>
            )}

            {/* Avatar upload */}
            <div className="min-w-0 md:col-span-2">
              <label>Avatar (optional)</label>
              <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  type="file"
                  accept="image/*"
                  className="min-w-0 flex-1"
                  style={{ ...inputStyle, height: 45, padding: "8px 10px" }}
                  onChange={(e) => handleAvatarPick(e.target.files?.[0])}
                />
                <div className="shrink-0" style={{ width: 48, height: 48, borderRadius: "50%", position: "relative", border: "1px solid #ddd", overflow: "hidden", background: "#f2f2f2" }}>
                  <div style={{
                    position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, color: "#777", fontWeight: 700
                  }}>
                    {(form.first_name?.[0] || "").toUpperCase()}{(form.last_name?.[0] || "").toUpperCase()}
                  </div>
                  {avatarDataUrl ? (
                    <img
                      src={avatarDataUrl}
                      alt="avatar preview"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: imgVisible ? "block" : "none" }}
                      onError={() => setImgVisible(false)}
                      onLoad={() => setImgVisible(true)}
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt="avatar preview"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: imgVisible ? "block" : "none" }}
                      onError={() => setImgVisible(false)}
                      onLoad={() => setImgVisible(true)}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Map + Lat/Lon */}
            <div className="min-w-0 md:col-span-2">
              <label>Home Location</label>
              <div className="mb-2 grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
                <div><label>Latitude</label><input type="number" step="0.000001" style={inputStyle} value={form.home_lat} onChange={(e) => setField("home_lat", e.target.value)} /></div>
                <div><label>Longitude</label><input type="number" step="0.000001" style={inputStyle} value={form.home_lon} onChange={(e) => setField("home_lon", e.target.value)} /></div>
              </div>
              <div
                ref={mapRef}
                style={{ width: "100%", minWidth: 0, height: "clamp(220px, 34dvh, 280px)", borderRadius: 10, overflow: "hidden", border: "1px solid #e5e5e5" }}
              />
              <div className="break-words" style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                Tip: Click the map to set the pin, or drag the pin. Lat/Lon fields update automatically.
              </div>
            </div>

            {/* Notes */}
            <div className="min-w-0 md:col-span-2">
              <label>Notes</label>
              <textarea style={textAreaStyle} value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Additional details…" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={footer} className="flex-col-reverse sm:flex-row">
          <button onClick={onClose} className="w-full sm:w-auto" style={actionBtn("#888")} disabled={saving}>Cancel</button>
          <button
            onClick={onSave}
            disabled={saving || !taxonomyReady || costCentersLoading || costSubtypesLoading || !!taxonomyError}
            className="w-full sm:w-auto"
            style={{ ...actionBtn("var(--btn-action-normal, #ff7a00)"), opacity: saving ? 0.7 : 1 }}
            onMouseEnter={(e) => (e.currentTarget.style.background = actionHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = actionNormal)}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResourceEditModal;
